import { AlertTriangle, CalendarX2, CheckCircle2, ClipboardCheck, HelpCircle } from 'lucide-react'
import type { CompanyDashboardSummary } from '@/types/company.types'
import { MetricRow, Panel } from '@/components/ui'
import type { MetricRowTone } from '@/components/ui'

export interface AttentionPanelProps {
  companyId: string
  summary?: CompanyDashboardSummary
  isLoading: boolean
}

interface AttentionItem {
  key: string
  count: number
  label: (n: number) => string
  hint: string
  to: string
  actionLabel: string
  tone: MetricRowTone
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
}

/**
 * Ranked by how quickly the situation degrades if ignored: a missed shift is
 * already costing coverage today, while a pending approval only becomes urgent
 * at payroll. Order is deliberate — do not sort by count.
 */
function buildItems(companyId: string, s: CompanyDashboardSummary): AttentionItem[] {
  const q = `?companyId=${companyId}`
  return [
    {
      key: 'noShows',
      count: s.noShowsToday,
      label: (n) => `${n} no-${n === 1 ? 'show' : 'shows'} today`,
      hint: 'Scheduled shifts with no clock-in',
      to: `/attendance${q}`,
      actionLabel: 'View attendance',
      tone: 'critical',
      icon: AlertTriangle,
    },
    {
      key: 'unconfirmed',
      count: s.unconfirmedShiftsToday,
      label: (n) => `${n} unconfirmed ${n === 1 ? 'shift' : 'shifts'} today`,
      hint: 'Awaiting employee confirmation',
      to: `/shifts${q}`,
      actionLabel: 'Open shift planning',
      tone: 'pending',
      icon: HelpCircle,
    },
    {
      key: 'corrections',
      count: s.pendingCorrections,
      label: (n) => `${n} time ${n === 1 ? 'correction' : 'corrections'} to review`,
      hint: 'Submitted by employees',
      to: `/time-corrections${q}`,
      actionLabel: 'Review time corrections',
      tone: 'pending',
      icon: ClipboardCheck,
    },
    {
      key: 'leaves',
      count: s.pendingLeaves,
      label: (n) => `${n} leave ${n === 1 ? 'request' : 'requests'} awaiting approval`,
      hint: 'Blocking schedule confirmation',
      to: `/leaves${q}`,
      actionLabel: 'Review leave requests',
      tone: 'pending',
      icon: CalendarX2,
    },
  ]
}

/**
 * The page's subject. Only exceptions that are actually non-zero render, so on
 * a healthy day this collapses to one line and the operator is done reading.
 */
export function AttentionPanel({
  companyId,
  summary,
  isLoading,
}: AttentionPanelProps): React.ReactElement {
  const items = summary ? buildItems(companyId, summary).filter((i) => i.count > 0) : []
  const total = items.reduce((sum, i) => sum + i.count, 0)

  // Resolved is not a finding. When there is nothing to act on this collapses
  // to a single line — a full empty panel reads as "missing", not "all clear",
  // and on a healthy company that panel would dominate the page saying nothing.
  if (!isLoading && items.length === 0) {
    return (
      <p className="flex items-center gap-2 px-1 text-sm text-gray-500 dark:text-gray-400">
        <CheckCircle2
          className="h-4 w-4 shrink-0 text-teal-600 dark:text-teal-400"
          aria-hidden
        />
        <span>
          <span className="font-medium text-gray-700 dark:text-gray-300">All clear</span> — no
          no-shows, unconfirmed shifts, corrections or leave requests.
        </span>
      </p>
    )
  }

  return (
    <Panel
      as="section"
      aria-labelledby="attention-heading"
      title={
        <h3
          id="attention-heading"
          className="text-sm font-semibold tracking-[-0.006em] text-gray-900 dark:text-gray-100"
        >
          Needs attention
        </h3>
      }
      action={
        !isLoading && total > 0 ? (
          <span className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">
            {total} open
          </span>
        ) : null
      }
      dense
      contentClassName="p-2"
    >
      {isLoading ? (
        <div className="flex items-center gap-3 px-2 py-3" aria-hidden>
          <div className="h-4 w-4 rounded bg-gray-100 dark:bg-gray-700" />
          <div className="h-4 flex-1 rounded bg-gray-100 dark:bg-gray-700" />
          <span className="sr-only">Loading items that need attention</span>
        </div>
      ) : (
        <ul className="space-y-0.5">
          {items.map((item, index) => (
            <li
              key={item.key}
              className="attention-row"
              style={{ '--row-index': index } as React.CSSProperties}
            >
              <MetricRow
                label={item.label(item.count)}
                hint={item.hint}
                value={item.count}
                tone={item.tone}
                icon={item.icon}
                to={item.to}
                actionLabel={item.actionLabel}
              />
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

AttentionPanel.displayName = 'AttentionPanel'
